
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import { documentStore, singularityStore } from "@/store/store-accessor";
import { findNode } from "@/lib/tree/tree";
import lodash from "lodash";
import Icon from "@/components/UI/Icon.vue";
import ColorSchemePickerNew from "./ColorSchemePickerNew.vue";
import { getPossibleColorSchemes } from "@/lib/singularity/brand/colors/schemes";
import { applySelectedSchemeToElement } from "@/lib/singularity/brand/colors/utils";
import {
  getChosenColorSchemeFromClasses,
  getRandomSchemeForCategory,
  getRandomScheme,
  getAvailableCategories,
  ColorSchemeCategory,
  ChosenColorScheme,
  DEFAULT_CHOSEN_COLOR_SCHEME
} from "@/lib/singularity/brand/colors/utils";
import { ColorScheme } from "@/lib/singularity/brand/colors/types";
import IconButtonShuffle from "../components/IconButtonShuffle.vue";

@Component({
  components: {
    Icon,
    ColorSchemePickerNew,
    IconButtonShuffle
  }
})
export default class PageStructureSectionColorsNew extends Vue {
  @Prop({ required: true })
  elementId!: number;

  @Prop({ default: false })
  isOverlayHeader!: boolean;

  showPicker = false;

  get element() {
    return findNode(documentStore.document.tree, this.elementId);
  }

  get chosenScheme(): ChosenColorScheme {
    if (!this.element) {
      return DEFAULT_CHOSEN_COLOR_SCHEME;
    }

    const classes = lodash.get(
      this.element,
      "data.properties.settings.advanced.classes",
      []
    );

    return getChosenColorSchemeFromClasses(classes);
  }

  get colorSchemeString(): string {
    return this.chosenScheme.schemeString;
  }

  get selectedCategory(): ColorSchemeCategory {
    return this.chosenScheme.category;
  }

  get schemeIndex(): number {
    return this.chosenScheme.index;
  }

  get categoryLabel(): string {
    if (!this.selectedCategory) return "";

    const labels: Record<string, string> = {
      primary: "Primary",
      secondary1: "Secondary 1",
      secondary2: "Secondary 2",
      neutral: "Neutral"
    };

    return labels[this.selectedCategory] || "";
  }

  get allSchemes() {
    return getPossibleColorSchemes(singularityStore.brand.color);
  }

  get availableCategories() {
    return getAvailableCategories(
      this.allSchemes,
      this.selectedCategory,
      this.schemeIndex
    );
  }

  getCategoryStyle(scheme: ColorScheme | null) {
    if (!scheme) return {};
    return {
      "--swatch-bg": scheme.background.value?.toString(),
      "--swatch-text": scheme.contrastedFromBackgroundForText.value?.toString()
    };
  }

  toggleCategory(category: string | null) {
    if (!category) return;
 
     

    // Check if already selected for this category
    const hasCurrentScheme = this.selectedCategory === category;

    this.showPicker = !this.showPicker;

    if (!hasCurrentScheme) {
      this.showPicker = true;
      // Select a random scheme from this category
      this.selectRandomSchemeForCategory(category);
    }
  }

  selectRandomSchemeForCategory(category: string) {
    const result = getRandomSchemeForCategory(
      category as ColorSchemeCategory,
      this.allSchemes
    );
    if (result) {
      this.handleSchemeSelected(result.schemeString, result.scheme);
    }
  }

  randomizeAll() {
    const result = getRandomScheme(this.allSchemes);
    if (result) {
      this.handleSchemeSelected(result.schemeString, result.scheme);
    }
  }

  handleSchemeSelected(schemeString: string, scheme: ColorScheme) {
    if (!this.element) return;

    applySelectedSchemeToElement(
      this.elementId,
      this.element,
      schemeString,
      scheme
    );
  }
}
